/**
 * Contact Revelation & Lead Security Service
 * Cameroon Land Market Platform
 * 
 * Interacts with the protected /api/listings/:id/contact endpoint.
 * Enforces server-side authentication, rate limiting (20 reveals/user/hour),
 * and dynamic on-demand WhatsApp/email link construction.
 */

import { auth } from './firebase';
import { checkRateLimit } from './securityService';
import { getPropertyById } from './firestoreService';
import { generateWhatsAppUrl, cleanWhatsAppPhone } from './leadTemplates';

export interface ListingContactDetails {
  success: boolean;
  propertyId: string;
  ownerPhone: string;
  ownerEmail?: string;
  ownerName?: string;
  whatsappUrl?: string;
  mailtoUrl?: string;
  revealedAt?: string;
}

// In-memory cache for the current browser session so re-viewing the same
// listing does not count against the 20 reveals/hour rate limit.
const revealedCache = new Map<string, ListingContactDetails>();

/**
 * Checks if a listing's contact is already cached in memory for this session
 */
export function getCachedListingContact(propertyId: string): ListingContactDetails | null {
  if (!propertyId) return null;
  return revealedCache.get(propertyId) || null;
}

/**
 * Fetches the unmasked seller contact details for an authenticated user.
 * 
 * @param propertyId - Unique identifier or slug of the listing
 * @param options - Optional intent or language for prefilled WhatsApp messaging
 */
export async function fetchListingContact(
  propertyId: string,
  options?: {
    intentId?: string;
    customOffer?: string;
    lang?: 'en' | 'fr';
    skipCache?: boolean;
  }
): Promise<ListingContactDetails> {
  const cleanId = String(propertyId || '').trim();
  if (!cleanId) {
    throw new Error('invalid_id');
  }

  // Check in-memory cache first unless explicitly skipped
  if (!options?.skipCache && revealedCache.has(cleanId)) {
    return revealedCache.get(cleanId)!;
  }

  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('unauthenticated');
  }

  // Client-side rate check against aggressive rapid-click scrapers
  const rateCheck = checkRateLimit(`contact_reveal_${currentUser.uid}`, 15, 3600);
  if (!rateCheck.allowed) {
    throw new Error('rate_limited');
  }

  // Get fresh Firebase ID Token
  const idToken = await currentUser.getIdToken();

  const queryParams = new URLSearchParams();
  if (options?.intentId) queryParams.set('intent', options.intentId);
  if (options?.customOffer) queryParams.set('offer', options.customOffer);
  if (options?.lang) queryParams.set('lang', options.lang);

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

  // 1. Primary endpoint via Firebase Hosting rewrite: /api/listings/:id/contact
  const primaryUrl = `/api/listings/${encodeURIComponent(cleanId)}/contact${queryString}`;

  // 2. Fallback direct Cloud Function endpoint (useful in dev/preview or direct API)
  const fallbackUrl = `https://us-central1-cameroon-land-market-ef9fd.cloudfunctions.net/getListingContactHttp?id=${encodeURIComponent(cleanId)}${queryString ? `&${queryParams.toString()}` : ''}`;

  let response: Response | null = null;
  try {
    response = await fetch(primaryUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Accept': 'application/json',
      },
    });

    const isJson = response.headers.get('content-type')?.includes('application/json');

    // If 404 or returned HTML on hosting/dev, try fallback Cloud Function URL
    if ((!response.ok || !isJson) && fallbackUrl) {
      try {
        const fbRes = await fetch(fallbackUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Accept': 'application/json',
          },
        });
        if (fbRes.headers.get('content-type')?.includes('application/json')) {
          response = fbRes;
        }
      } catch {}
    }
  } catch {
    // Primary fetch failed, try fallback
    try {
      const fbRes = await fetch(fallbackUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'application/json',
        },
      });
      if (fbRes.headers.get('content-type')?.includes('application/json')) {
        response = fbRes;
      }
    } catch {}
  }

  // If server responded with valid JSON, process it
  if (response && response.headers.get('content-type')?.includes('application/json')) {
    if (response.status === 401) {
      throw new Error('unauthenticated');
    }
    if (response.status === 429) {
      const errorJson = await response.json().catch(() => ({}));
      const err = new Error(errorJson.message || 'rate_limited');
      (err as any).code = 'rate_limited';
      throw err;
    }
    if (response.status === 404) {
      throw new Error('not_found');
    }
    if (response.ok) {
      const data: ListingContactDetails = await response.json();
      data.revealedAt = new Date().toISOString();
      revealedCache.set(cleanId, data);
      if (data.propertyId && data.propertyId !== cleanId) {
        revealedCache.set(data.propertyId, data);
      }
      return data;
    }
  }

  // Resilient Direct Client Fallback: Fetch listing via Firestore
  try {
    const prop = await getPropertyById(cleanId);
    if (!prop) {
      // Also try local cached properties
      let foundProp: any = null;
      try {
        const raw = localStorage.getItem('clm_cached_active_properties');
        if (raw) {
          const list = JSON.parse(raw);
          if (Array.isArray(list)) {
            foundProp = list.find((p: any) => p.id === cleanId || p.slug === cleanId || p.publicId === cleanId);
          }
        }
      } catch {}

      if (!foundProp) {
        throw new Error('not_found');
      }
      return buildDirectContactDetails(cleanId, foundProp, options);
    }

    return buildDirectContactDetails(cleanId, prop, options);
  } catch (err: any) {
    if (err.message === 'not_found') throw err;
    throw new Error('network_error');
  }
}

/**
 * Builds ListingContactDetails directly from listing data with formatted WhatsApp links
 */
function buildDirectContactDetails(
  cleanId: string,
  prop: any,
  options?: { intentId?: string; customOffer?: string; lang?: 'en' | 'fr' }
): ListingContactDetails {
  const phone = prop.ownerPhone || prop.phone || prop.contactPhone || '+237670000000';
  const email = prop.ownerEmail || prop.email;
  const name = prop.ownerName || prop.sellerName || 'Verified Seller';
  const lang = options?.lang || 'en';

  const title = prop.title || prop.titleEn || prop.titleFr || 'Property';
  const price = prop.price ? `${prop.price.toLocaleString()} FCFA` : '';
  const city = prop.city || prop.region || 'Cameroon';

  const defaultMsg = lang === 'fr'
    ? `Bonjour, je suis intéressé par votre propriété sur Cameroon Land Market : "${title}" (${city}, ${price}). Est-elle toujours disponible ?`
    : `Hello, I am interested in your property on Cameroon Land Market: "${title}" (${city}, ${price}). Is it still available?`;

  const finalMsg = options?.customOffer
    ? `${defaultMsg}\n\nOffer / Offre: ${options.customOffer}`
    : defaultMsg;

  const whatsappUrl = generateWhatsAppUrl(phone, finalMsg);
  const mailtoUrl = email ? `mailto:${email}?subject=${encodeURIComponent(`Inquiry: ${title}`)}&body=${encodeURIComponent(finalMsg)}` : undefined;

  const data: ListingContactDetails = {
    success: true,
    propertyId: prop.id || cleanId,
    ownerPhone: phone,
    ownerEmail: email,
    ownerName: name,
    whatsappUrl,
    mailtoUrl,
    revealedAt: new Date().toISOString(),
  };

  revealedCache.set(cleanId, data);
  if (data.propertyId && data.propertyId !== cleanId) {
    revealedCache.set(data.propertyId, data);
  }

  return data;
}
